import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster, toast } from "sonner";
import axios from "axios";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import {
  ArrowLeft,
  CheckCircle,
  Camera,
  IdentificationCard,
  Warning,
  X,
  Lightning,
  ArrowsClockwise,
  Truck,
} from "@phosphor-icons/react";

import { PUBLIC_BACKEND as BACKEND } from "@/lib/api";
const ZIPX_LOGO_URL = `${BACKEND}/api/zipexpress/logo`;

/**
 * Auto-format a Jamaican driver's license into `###-###-###` format.
 * Strips non-digits, caps at 9 numeric chars (visual max length 11 incl.
 * two hyphens), and inserts hyphens after positions 3 and 6.
 * Example: "116989111" -> "116-989-111"
 */
function formatLicense(raw) {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}
const LICENSE_PATTERN = /^\d{3}-\d{3}-\d{3}$/;
const KG_TO_LB = 2.20462;
const LB_TO_KG = 1 / KG_TO_LB;
const VEHICLE_TYPES = ["motorcycle", "car", "van", "truck", "bicycle"];

export default function DriverSignup() {
  const [form, setForm] = useState({
    name: "",
    dob: "",
    license_number: "",
    email: "",
    phone: "",
    home_address: "",
    home_lat: null,
    home_lng: null,
    password: "",
    password_confirm: "",
    vehicle_type: "",
    license_plate: "",
    capacity_kg: "",   // canonical value (always stored in kg)
  });
  const [capacityUnit, setCapacityUnit] = useState("kg"); // "kg" | "lb"
  const [capacityInput, setCapacityInput] = useState(""); // raw text input
  const [selfie, setSelfie] = useState(null);          // {blob, dataUrl}
  const [licenseFront, setLicenseFront] = useState(null);
  const [licenseBack, setLicenseBack] = useState(null);
  // Vehicle photos captured live with GPS + timestamp
  const [vehicleFront, setVehicleFront] = useState(null);
  const [vehicleBack, setVehicleBack] = useState(null);
  const [vehicleFrontGeo, setVehicleFrontGeo] = useState(null);
  const [vehicleBackGeo, setVehicleBackGeo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [logoOk, setLogoOk] = useState(true);

  const passwordsMatch = form.password === form.password_confirm;
  const passwordMismatchVisible = form.password_confirm.length > 0 && !passwordsMatch;

  const handleCapacityChange = (raw) => {
    setCapacityInput(raw);
    const n = parseFloat(raw);
    if (!Number.isFinite(n) || n <= 0) {
      setForm((f) => ({ ...f, capacity_kg: "" }));
      return;
    }
    const kg = capacityUnit === "kg" ? n : n * LB_TO_KG;
    setForm((f) => ({ ...f, capacity_kg: Number(kg.toFixed(2)) }));
  };

  const toggleCapacityUnit = (newUnit) => {
    if (newUnit === capacityUnit) return;
    // Convert the displayed value to the new unit
    const n = parseFloat(capacityInput);
    if (Number.isFinite(n) && n > 0) {
      const converted = newUnit === "kg" ? n * LB_TO_KG : n * KG_TO_LB;
      setCapacityInput(converted.toFixed(2));
    }
    setCapacityUnit(newUnit);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!LICENSE_PATTERN.test(form.license_number)) {
      toast.error("Driver's license must be 9 digits in the format 116-989-111");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (!passwordsMatch) {
      toast.error("Passwords do not match");
      return;
    }
    if (!VEHICLE_TYPES.includes(form.vehicle_type)) {
      toast.error("Please pick a vehicle type");
      return;
    }
    if (!form.capacity_kg || form.capacity_kg <= 0) {
      toast.error("Please enter a vehicle capacity");
      return;
    }
    if (!form.license_plate.trim()) {
      toast.error("Please enter your vehicle plate #");
      return;
    }
    if (!selfie) { toast.error("Please take a live selfie"); return; }
    if (!licenseFront) { toast.error("Please take a live photo of your license front"); return; }
    if (!licenseBack) { toast.error("Please take a live photo of your license back"); return; }
    if (!vehicleFront) { toast.error("Please take a live photo of the FRONT of your vehicle"); return; }
    if (!vehicleBack) { toast.error("Please take a live photo of the BACK of your vehicle"); return; }
    if (!form.home_address) { toast.error("Please enter your home address"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("dob", form.dob);
      fd.append("license_number", form.license_number);
      fd.append("email", form.email);
      if (form.phone) fd.append("phone", form.phone);
      fd.append("home_address", form.home_address);
      if (form.home_lat != null) fd.append("home_lat", String(form.home_lat));
      if (form.home_lng != null) fd.append("home_lng", String(form.home_lng));
      // Module 1.01 — password
      fd.append("password", form.password);
      fd.append("password_confirm", form.password_confirm);
      // Module 1.02 — vehicle
      fd.append("vehicle_type", form.vehicle_type);
      fd.append("capacity_kg", String(form.capacity_kg));
      fd.append("license_plate", form.license_plate.trim());
      fd.append("selfie", selfie.blob, "selfie.jpg");
      fd.append("license_front", licenseFront.blob, "license-front.jpg");
      fd.append("license_back", licenseBack.blob, "license-back.jpg");
      // Vehicle live photos + GPS (PRD)
      if (vehicleFront) {
        fd.append("vehicle_front", vehicleFront.blob, "vehicle-front.jpg");
        if (vehicleFrontGeo) {
          if (vehicleFrontGeo.lat != null) fd.append("vehicle_front_lat", String(vehicleFrontGeo.lat));
          if (vehicleFrontGeo.lng != null) fd.append("vehicle_front_lng", String(vehicleFrontGeo.lng));
        }
      }
      if (vehicleBack) {
        fd.append("vehicle_back", vehicleBack.blob, "vehicle-back.jpg");
        if (vehicleBackGeo) {
          if (vehicleBackGeo.lat != null) fd.append("vehicle_back_lat", String(vehicleBackGeo.lat));
          if (vehicleBackGeo.lng != null) fd.append("vehicle_back_lng", String(vehicleBackGeo.lng));
        }
      }
      await axios.post(`${BACKEND}/api/public/drivers/signup`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSuccess(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Signup failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6" data-testid="driver-signup-success">
        <div className="max-w-md w-full bg-white border border-zinc-200 rounded-xl p-8 text-center shadow-sm">
          <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/30 mx-auto flex items-center justify-center mb-5">
            <CheckCircle size={26} weight="bold" className="text-emerald-600" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight mb-2" style={{ fontFamily: "Work Sans" }}>Almost there!</h1>
          <p className="text-zinc-600 mb-5">
            We've sent a verification link to <b>{form.email}</b>. Click it to confirm your email — our team will then review your application within 1-2 business days.
          </p>
          <Link to="/"><Button className="w-full bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="driver-signup-success-home">Back to Kanal</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="driver-signup-page">
      <Toaster position="top-right" />
      <div className="max-w-3xl mx-auto px-6 py-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 mb-8" data-testid="driver-back-to-landing">
          <ArrowLeft size={14} /> Back to Kanal
        </Link>

        {/* Already-a-driver hint */}
        <div className="mb-4 text-right text-xs text-zinc-500">
          Already verified?{" "}
          <Link to="/login" className="text-[#002BF6] font-semibold hover:underline" data-testid="driver-signup-signin-link">
            Sign in to your driver portal
          </Link>
          {" · "}
          <Link to="/forgot-password" className="text-zinc-600 hover:text-zinc-900 hover:underline" data-testid="driver-signup-forgot-link">
            Forgot password?
          </Link>
        </div>

        {/* Brand header */}
        <div className="bg-white border border-zinc-200 rounded-xl p-6 mb-6 flex items-center gap-5" data-testid="driver-signup-brand">
          {logoOk ? (
            <a href ="https://zipexpress.co" target="_blank"><img src={ZIPX_LOGO_URL} alt="ZipExpress" className="h-14 w-auto" onError={() => setLogoOk(false)} data-testid="driver-signup-zipx-logo" /> </a>
          ) : (
            <div className="h-14 w-14 rounded-md bg-zinc-950 text-white flex items-center justify-center"><Lightning size={22} weight="bold" /></div>
          )}
          <div className="flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Deliver for</div>
            <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans" }}>ZipExpress</h1>
            <div className="text-xs text-zinc-500 mt-0.5">Exclusive last-mile partner of Kanal · Earn 86% per delivery</div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="bg-white border border-zinc-200 rounded-xl p-6 sm:p-8 space-y-6" data-testid="driver-signup-form">
          <div>
            <h2 className="text-lg font-semibold tracking-tight mb-1" style={{ fontFamily: "Work Sans" }}>Tell us about yourself</h2>
            <p className="text-xs text-zinc-500">All fields are required.</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full name" testid="driver-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
            <Field label="Date of birth" type="date" testid="driver-dob" value={form.dob} onChange={(v) => setForm({ ...form, dob: v })} required />
            <div data-testid="driver-license-number-field">
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                Driver's license #<span className="text-red-500 ml-1">*</span>
              </Label>
              <Input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder="116-989-111"
                maxLength={11}
                value={form.license_number}
                onChange={(e) => setForm({ ...form, license_number: formatLicense(e.target.value) })}
                pattern="\d{3}-\d{3}-\d{3}"
                title="Format: 116-989-111 (9 digits)"
                required
                className="mt-1.5 font-mono"
                data-testid="driver-license-number"
              />
              <p className="text-[11px] text-zinc-500 mt-1">9 digits, formatted as <span className="font-mono">116-989-111</span></p>
            </div>
            <Field label="Email" type="email" testid="driver-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
            <Field label="Phone (optional)" testid="driver-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Home address *</Label>
            <div className="mt-1.5">
              <AddressAutocomplete
                value={form.home_address}
                onChange={(v) => setForm({ ...form, home_address: v })}
                onLocationChange={(lat, lng) => setForm((f) => ({ ...f, home_lat: lat, home_lng: lng }))}
                country="JM"
                placeholder="Start typing your home address…"
                rows={2}
                dataTestId="driver-home-address"
              />
            </div>
          </div>

          {/* Module 1.01 — password */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                Password <span className="text-red-500 ml-1">*</span>
              </Label>
              <Input
                type="password"
                className="mt-1.5"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="At least 8 characters"
                autoComplete="new-password"
                required
                minLength={8}
                data-testid="driver-password"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                Confirm password <span className="text-red-500 ml-1">*</span>
              </Label>
              <Input
                type="password"
                className={`mt-1.5 ${passwordMismatchVisible ? "border-red-400 focus:border-red-500" : ""}`}
                value={form.password_confirm}
                onChange={(e) => setForm({ ...form, password_confirm: e.target.value })}
                placeholder="Re-type the same password"
                autoComplete="new-password"
                required
                data-testid="driver-password-confirm"
              />
              {passwordMismatchVisible && (
                <p className="text-[11px] text-red-600 mt-1" data-testid="driver-password-mismatch">
                  Passwords do not match.
                </p>
              )}
            </div>
          </div>

          {/* Module 1.02 — vehicle */}
          <div>
            <h3 className="text-sm font-semibold tracking-tight" style={{ fontFamily: "Work Sans" }}>Vehicle details</h3>
            <p className="text-xs text-zinc-500">Lets us match you to the right size deliveries.</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                Vehicle type <span className="text-red-500 ml-1">*</span>
              </Label>
              <Input
                list="vehicle-type-options"
                value={form.vehicle_type}
                onChange={(e) => setForm({ ...form, vehicle_type: e.target.value.toLowerCase() })}
                placeholder="Pick one"
                className="mt-1.5 capitalize"
                required
                data-testid="driver-vehicle-type"
              />
              <datalist id="vehicle-type-options">
                {VEHICLE_TYPES.map((v) => <option key={v} value={v} />)}
              </datalist>
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                Capacity <span className="text-red-500 ml-1">*</span>
              </Label>
              <div className="mt-1.5 flex gap-2">
                <Input
                  type="number"
                  min="0"
                  step="0.1"
                  inputMode="decimal"
                  value={capacityInput}
                  onChange={(e) => handleCapacityChange(e.target.value)}
                  placeholder={capacityUnit === "kg" ? "e.g. 250" : "e.g. 550"}
                  required
                  className="no-spinner min-w-0 flex-1"
                  data-testid="driver-capacity"
                />
                <div className="inline-flex rounded-md border border-zinc-200 overflow-hidden text-xs shrink-0">
                  {["kg", "lb"].map((u) => (
                    <button
                      key={u}
                      type="button"
                      onClick={() => toggleCapacityUnit(u)}
                      className={`px-3 py-2 font-semibold transition-colors ${capacityUnit === u ? "bg-[#002BF6] text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                      data-testid={`driver-capacity-unit-${u}`}
                    >
                      {u.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              {form.capacity_kg ? (
                <p className="text-[11px] text-zinc-500 mt-1">
                  ≈ {Number(form.capacity_kg).toFixed(2)} kg / {(Number(form.capacity_kg) * KG_TO_LB).toFixed(2)} lb
                </p>
              ) : null}
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                Vehicle plate # <span className="text-red-500 ml-1">*</span>
              </Label>
              <Input
                value={form.license_plate}
                onChange={(e) => setForm({ ...form, license_plate: e.target.value.toUpperCase() })}
                placeholder="e.g. AB1234"
                className="mt-1.5 font-mono uppercase"
                required
                data-testid="driver-vehicle-plate"
              />
            </div>
          </div>

          {/* Live camera captures — no file uploads */}
          <LiveCameraCapture
            label="Live selfie *"
            helper="Look at the camera and tap Capture. We use this to match against your license photo."
            facing="user"
            aspect="4/3"
            value={selfie}
            onChange={setSelfie}
            testidPrefix="driver-selfie"
            icon={<Camera size={28} weight="bold" />}
          />

          <div className="grid sm:grid-cols-2 gap-4">
            <LiveCameraCapture
              label="Driver's license — FRONT *"
              helper="Hold your license steady in landscape. No uploads — must be a live photo."
              facing="environment"
              aspect="3/2"
              value={licenseFront}
              onChange={setLicenseFront}
              testidPrefix="driver-license-front"
              icon={<IdentificationCard size={28} weight="bold" />}
            />
            <LiveCameraCapture
              label="Driver's license — BACK *"
              helper="Flip the license and capture the back side."
              facing="environment"
              aspect="3/2"
              value={licenseBack}
              onChange={setLicenseBack}
              testidPrefix="driver-license-back"
              icon={<IdentificationCard size={28} weight="bold" className="rotate-180" />}
            />
          </div>

          {/* Vehicle live photos — front + back with geolocation capture */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <LiveCameraCapture
              label="Vehicle — FRONT *"
              helper="Live photo of the front of your vehicle (plate visible)."
              facing="environment"
              aspect="3/2"
              value={vehicleFront}
              onChange={(v) => {
                setVehicleFront(v);
                if (v && typeof navigator !== "undefined" && navigator.geolocation) {
                  navigator.geolocation.getCurrentPosition(
                    (pos) => setVehicleFrontGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                    () => setVehicleFrontGeo(null),
                    { timeout: 5000, enableHighAccuracy: false }
                  );
                } else if (!v) {
                  setVehicleFrontGeo(null);
                }
              }}
              testidPrefix="driver-vehicle-front"
              icon={<Truck size={28} weight="bold" />}
            />
            <LiveCameraCapture
              label="Vehicle — BACK *"
              helper="Live photo of the back of your vehicle (plate visible)."
              facing="environment"
              aspect="3/2"
              value={vehicleBack}
              onChange={(v) => {
                setVehicleBack(v);
                if (v && typeof navigator !== "undefined" && navigator.geolocation) {
                  navigator.geolocation.getCurrentPosition(
                    (pos) => setVehicleBackGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                    () => setVehicleBackGeo(null),
                    { timeout: 5000, enableHighAccuracy: false }
                  );
                } else if (!v) {
                  setVehicleBackGeo(null);
                }
              }}
              testidPrefix="driver-vehicle-back"
              icon={<Truck size={28} weight="bold" className="rotate-180" />}
            />
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded p-3 flex gap-2 text-xs text-amber-900" data-testid="driver-warning">
            <Warning size={14} weight="bold" className="mt-0.5 shrink-0" />
            <span>By submitting you agree to our screening process. All three images must be live camera captures — uploads are not accepted. Stored securely and shown only to Kanal super admins for verification.</span>
          </div>

          <Button type="submit" disabled={submitting} className="w-full bg-[#002BF6] hover:bg-[#0021C7] text-white h-12" data-testid="driver-submit-btn">
            {submitting ? "Submitting your application…" : "Apply to deliver for ZipExpress"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", required = false }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">{label}{required && <span className="text-red-500 ml-1">*</span>}</Label>
      <Input type={type} className="mt-1.5" value={value} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testid} />
    </div>
  );
}

/**
 * Reusable live camera capture component.
 * - Uses getUserMedia + <video> + <canvas> for true live capture.
 * - No file upload fallback — uploads are intentionally not allowed.
 * - Renders the <video> element always (just hidden when idle) so the ref is
 *   available before we attach `srcObject` (this was the original bug).
 * - Supports `facing` ("user" | "environment"); on desktop the user-facing webcam
 *   is used regardless of facing hint.
 *
 * Props:
 *   - label, helper: copy
 *   - facing: "user" | "environment"
 *   - aspect: tailwind aspect ratio for the preview frame ("4/3" | "3/2")
 *   - value: {blob, dataUrl} | null
 *   - onChange: (value) => void
 *   - testidPrefix: string used for data-testid attrs
 *   - icon: ReactNode shown in the idle state
 */
function LiveCameraCapture({ label, helper, facing = "user", aspect = "4/3", value, onChange, testidPrefix, icon }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const [starting, setStarting] = useState(false);
  const [err, setErr] = useState(null);

  const stop = React.useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setStreaming(false);
  }, []);

  const start = async () => {
    if (starting || streaming) return;
    setErr(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setErr("Your browser doesn't support camera access. Please use Chrome or Safari on a phone.");
      return;
    }
    if (window.location.protocol !== "https:" && window.location.hostname !== "localhost") {
      setErr("Camera access requires HTTPS. Please use the secure URL.");
      return;
    }
    setStarting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: facing }, width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      streamRef.current = stream;
      setStreaming(true);
      // Attach the stream in the effect below — at this point the <video> is rendered
    } catch (e) {
      console.error("camera err", e);
      let msg = "Camera unavailable. Please grant camera permissions and try again.";
      if (e.name === "NotAllowedError") msg = "Camera access denied. Please allow camera permissions in your browser settings and reload.";
      else if (e.name === "NotFoundError") msg = "No camera detected on this device.";
      else if (e.name === "NotReadableError") msg = "Camera is already in use by another app.";
      setErr(msg);
    } finally {
      setStarting(false);
    }
  };

  // Attach the stream to the <video> once streaming flips to true and the
  // video element is rendered. This is the core fix to the original bug.
  useEffect(() => {
    if (streaming && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch((e) => {
        console.warn("video.play() rejected", e);
      });
    }
  }, [streaming]);

  // Cleanup on unmount
  useEffect(() => () => stop(), [stop]);

  const capture = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c || !v.videoWidth) {
      toast.error("Camera isn't ready yet. Try again in a second.");
      return;
    }
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob(
      (blob) => {
        if (!blob) { toast.error("Couldn't capture image"); return; }
        const dataUrl = c.toDataURL("image/jpeg", 0.85);
        onChange({ blob, dataUrl });
        stop();
      },
      "image/jpeg",
      0.85
    );
  };

  const retake = () => {
    onChange(null);
    start();
  };

  const aspectClass = aspect === "3/2" ? "aspect-[3/2]" : "aspect-[4/3]";

  return (
    <div data-testid={`${testidPrefix}-section`}>
      <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">{label}</Label>
      {helper && <p className="text-xs text-zinc-500 mt-0.5 mb-2">{helper}</p>}
      <div className={`bg-zinc-900 rounded-md ${aspectClass} overflow-hidden flex items-center justify-center relative`}>
        {/* Captured still */}
        {value ? (
          <>
            <img src={value.dataUrl} alt="capture preview" className="w-full h-full object-cover" data-testid={`${testidPrefix}-preview`} />
            <button
              type="button"
              onClick={retake}
              className="absolute top-2 right-2 px-2.5 py-1.5 rounded bg-white/95 text-zinc-900 text-xs font-medium shadow-md flex items-center gap-1 hover:bg-white"
              data-testid={`${testidPrefix}-retake`}
            >
              <ArrowsClockwise size={11} weight="bold" /> Retake
            </button>
            <div className="absolute bottom-2 left-2 px-2 py-1 rounded bg-emerald-600 text-white text-[10px] font-semibold flex items-center gap-1">
              <CheckCircle size={10} weight="bold" /> Live capture
            </div>
          </>
        ) : (
          <>
            {/* Always-mounted video: ref is ready before srcObject assignment */}
            <video
              ref={videoRef}
              className={`w-full h-full object-cover transition-opacity ${streaming ? "opacity-100" : "opacity-0 pointer-events-none absolute"}`}
              playsInline
              muted
              autoPlay
              data-testid={`${testidPrefix}-video`}
            />
            {streaming ? (
              <>
                {/* Targeting frame overlay */}
                <div className="absolute inset-4 border-2 border-white/60 rounded-md pointer-events-none" />
                <button
                  type="button"
                  onClick={capture}
                  className="absolute bottom-3 left-1/2 -translate-x-1/2 w-14 h-14 rounded-full bg-white border-4 border-[#002BF6] shadow-lg flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
                  data-testid={`${testidPrefix}-capture-btn`}
                  aria-label="Capture photo"
                >
                  <Camera size={22} weight="bold" className="text-[#002BF6]" />
                </button>
                <button
                  type="button"
                  onClick={stop}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60"
                  data-testid={`${testidPrefix}-cancel-btn`}
                  aria-label="Cancel"
                >
                  <X size={14} weight="bold" />
                </button>
              </>
            ) : (
              <div className="text-center p-6 text-zinc-200 absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-zinc-300 mb-3">{icon}</div>
                <Button
                  type="button"
                  onClick={start}
                  disabled={starting}
                  className="bg-white text-zinc-900 hover:bg-zinc-100"
                  data-testid={`${testidPrefix}-start-btn`}
                >
                  {starting ? "Starting camera…" : "Open camera"}
                </Button>
                {err && <div className="text-xs text-red-300 mt-3 max-w-xs">{err}</div>}
              </div>
            )}
          </>
        )}
      </div>
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
